import { iconsPath } from '../../utils/imagePath';
import { handleLightClick } from '../../utils/UtilsFunction';
import undoRedoManager from '../../utils/UndoRedoManager';
import store from '../../redux/store';
import { updateOpenModals } from '../../redux/Slice/gridSlice';
import toast from 'react-hot-toast';

// Action handlers for shortcuts
const shortcutActions = {
  undo: () => {
    undoRedoManager.undo();
  },
  redo: () => {
    undoRedoManager.redo();
  },
  copy: () => {
    // Trigger Ctrl+C keyboard event
    const event = new KeyboardEvent('keydown', {
      key: 'c',
      code: 'KeyC',
      ctrlKey: true,
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(event);
  },
  paste: () => {
    // Trigger Ctrl+V keyboard event
    const event = new KeyboardEvent('keydown', {
      key: 'v',
      code: 'KeyV',
      ctrlKey: true,
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(event);
  },
  delete: () => {
    window.dispatchEvent(new CustomEvent('openDeleteModal'));
  },
  save: () => {
    // Trigger Ctrl+S keyboard event
    const event = new KeyboardEvent('keydown', {
      key: 's',
      code: 'KeyS',
      ctrlKey: true,
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(event);
  },
  toggleFullScreen: () => {
    // Use the Fullscreen API directly instead of dispatching a keyboard event
    const docElm = document.documentElement;
    if (!document.fullscreenElement) {
      if (docElm.requestFullscreen) {
        docElm.requestFullscreen();
      } else if (docElm.mozRequestFullScreen) { /* Firefox */
        docElm.mozRequestFullScreen();
      } else if (docElm.webkitRequestFullscreen) { /* Chrome, Safari & Opera */
        docElm.webkitRequestFullscreen();
      } else if (docElm.msRequestFullscreen) { /* IE/Edge */
        docElm.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  },
  openImport: () => {
    // Open the upload/import modal
    store.dispatch(updateOpenModals({ name: 'upload', isOpen: true }));
  },
  resetCamera: () => {
    // Trigger 'R' keyboard event to reset camera
    const event = new KeyboardEvent('keydown', {
      key: 'r',
      code: 'KeyR',
      ctrlKey: false,
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(event);
  },
  rename: () => {
    const state = store.getState?.();
    const selected =
      state?.assets_object?.select_object ||
      state?.data?.select_object ||
      state?.grid?.select_object ||
      state?.gridSlice?.select_object;
    const selectedId = selected?._id || selected?.url || selected?.name;
    if (!selectedId) {
      toast.error('Please select an object to rename.');
      return;
    }
    // Trigger Shift+R keyboard event to rename selected object
    const event = new KeyboardEvent('keydown', {
      key: 'R',
      code: 'KeyR',
      shiftKey: true,
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(event);
  },
  duplicate: () => {
    // Trigger Shift+D keyboard event to duplicate selected object
    const event = new KeyboardEvent('keydown', {
      key: 'D',
      code: 'KeyD',
      shiftKey: true,
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(event);
  },
  focus: () => {
    // Trigger 'F' keyboard event to focus on selected object
    const event = new KeyboardEvent('keydown', {
      key: 'f',
      code: 'KeyF',
      ctrlKey: false,
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(event);
  },
  selectAll: () => {
    const event = new KeyboardEvent('keydown', {
      key: 'a',
      code: 'KeyA',
      ctrlKey: true,
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(event);
  },
};

export const Shapes = [
  { heading: '3D Shapes' },
  { icon: iconsPath?.Layer1Image, label: 'Plane' },
  { icon: iconsPath?.Layer12Image, label: 'Cube' },
  { icon: iconsPath?.Layer2Image, label: 'Sphere' },
  { icon: iconsPath?.Layer3Image, label: 'Cylinder' },
  { icon: iconsPath?.Layer4Image, label: 'Cone' },
  { icon: iconsPath?.Layer13Image, label: 'Pyramid' },
  { icon: iconsPath?.Layer5Image, label: 'Capsule' },
  { icon: iconsPath?.Layer6Image, label: 'Torus' },
  { gap: true },
  { heading: '2D Shapes' },
  { icon: iconsPath?.Layer7Image, label: 'Rectangle' },
  { icon: iconsPath?.Layer8Image, label: 'Ellipse' },
  { icon: iconsPath?.Layer9Image, label: 'Triangle' },
  { icon: iconsPath?.Layer10Image, label: 'Pentagon' },
  { icon: iconsPath?.Layer11Image, label: 'Star' },
];

export const Elements = [
  // { icon: iconsPath?.Element1Image, label: 'Terrain' },
  { icon: iconsPath?.Element2Image, label: 'Water' },
  { icon: iconsPath?.Element3Image, label: 'Clouds' },
  { icon: iconsPath?.Layer11Image, label: 'Stars' },
  { icon: iconsPath?.Element4Image, label: 'Rain' },
  { icon: iconsPath?.Element5Image, label: 'Snow' },
  { icon: iconsPath?.Element6Image, label: 'Fire' },
  { icon: iconsPath?.Element7Image, label: 'Foliage' },
];

export const ElementIcons = Elements.reduce((acc, el) => {
  acc[el.label.toLowerCase()] = el.icon;
  return acc;
}, {});

export const Light = [
  {
    icon: iconsPath?.Light2Image,
    label: 'Directional',
    action: 'Light',
  },
  {
    icon: iconsPath?.Light1Image,
    label: 'Point',
    action: 'Light',
  },
  {
    icon: iconsPath?.Light3Image,
    label: 'Spot',
    action: 'Light',
  },
  {
    icon: iconsPath?.arealight,
    label: 'Area',
    action: 'Light',
  },
  {
    icon: iconsPath?.ambientlight,
    label: 'Ambient',
    action: 'Light',
  },
];

export const Camera = [
  { icon: iconsPath?.perspective, label: 'Perspective' },
  { icon: iconsPath?.orthographic, label: 'Orthographic' },
];

export const MenuItems = [
  {
    icon: iconsPath?.group39Image,
    label: 'Upload/Import',
    action: 'upload/import',
  },
  { icon: iconsPath?.LibraryImage, label: 'Library' },
  {
    icon: iconsPath?.group13Image,
    label: 'Shapes',
    hasSubMenu: true,
    action: 'shapes',
    submenu: Shapes,
  },
  {
    icon: iconsPath?.group16Image,
    label: 'Elements',
    hasSubMenu: true,
    action: 'elements',
    submenu: Elements,
  },
  {
    icon: iconsPath?.group15Image,
    label: 'Lights',
    hasSubMenu: true,
    action: 'Light',
    submenu: Light,
  },
  {
    icon: iconsPath?.group24Image,
    label: 'Camera',
    hasSubMenu: true,
    action: 'Cemera',
    submenu: Camera,
  },
  { icon: iconsPath?.group14Image, label: 'Text', action: 'text' },
  { icon: iconsPath?.group40Image, label: 'Hotspots', action: 'hotspots' },
  { icon: iconsPath?.group42Image, label: 'Group' },
];

export const shortcutKeys = [
  { key: 'Save', label: 'Ctrl+S', onClick: shortcutActions.save },
  // { key: 'New File' },
  { key: 'Open/Import', label: 'Ctrl+O', onClick: shortcutActions.openImport },
  // { key: 'Move To...' },
  // { gap: true },
  // { key: 'Version History' },
  { gap: true },
  { key: 'Undo', label: 'Ctrl+Z', onClick: shortcutActions.undo },
  { key: 'Redo', label: 'Ctrl+Y', onClick: shortcutActions.redo },
  { gap: true },
  // { key: 'Copy', label: 'Ctrl+C', onClick: shortcutActions.copy },
  // { key: 'Cut', label: 'Ctrl+X' },
  { key: 'Delete', label: 'Delete', onClick: shortcutActions.delete },
  { key: 'Duplicate', label: 'Shift+D', onClick: shortcutActions.duplicate },
  // { key: 'Paste', label: 'Ctrl+V', onClick: shortcutActions.paste },
  // { key: 'Paste Over Selection', label: 'Ctrl+Shift+V' },
  { gap: true },
  { key: 'Select All Objects', label: 'Ctrl+A', onClick: shortcutActions.selectAll },
  { key: 'Rename', label: 'Shift+R', onClick: shortcutActions.rename },
  { gap: true },
  // { key: 'Toggle UI', label: 'Ctrl+\\' },
  { key: 'Toggle Fullscreen', label: 'Ctrl+Shift+F', onClick: shortcutActions.toggleFullScreen },
  { gap: true },
  // { key: 'Zoom In', label: 'Ctrl++' },
  // { key: 'Zoom Out', label: 'Ctrl+-' },
  // { gap: true },
  { key: 'Focus', label: 'F', onClick: shortcutActions.focus },
  { key: 'Reset Camera', label: 'R', onClick: shortcutActions.resetCamera },
  // { gap: true },
  // { key: 'Move Forward / Back', label: 'W / S' },
  // { key: 'Move Left / Right', label: 'A / D' },
  // { key: 'Move Up / Down', label: 'E / Q' },

];

export const ShortCutKey2 = [
  { heading: 'Keyboard Shortcuts' },
  { key: 'ESC', title: 'Deselect, quit edit, switch to cursor' },
  { gap: 'True' },
  { key: 'Ctrl+S', title: 'Save', onClick: shortcutActions.save },
  { gap: 'True' },
  { key: 'Ctrl+O', title: 'Open/Import',onClick: shortcutActions.openImport },
  { gap: 'True' },
  { key: 'Ctrl+Z', title: 'Undo', onClick: shortcutActions.undo },
  { gap: 'True' },
  { key: 'Ctrl+Y', title: 'Redo', onClick: shortcutActions.redo },
  { gap: 'True' },
  { key: 'Ctrl+C', title: 'Copy', onClick: shortcutActions.copy },
  { gap: 'True' },
  { key: 'Shift+D', title: 'Duplicate', onClick: shortcutActions.duplicate },
  { gap: 'True' },
  // { key: 'Ctrl+X', title: 'Cut' },
  // { gap: 'True' },
  { key: 'Delete', title: 'Delete', onClick: shortcutActions.delete },
  { key: 'Ctrl+V', title: 'Paste', onClick: shortcutActions.paste },
  { gap: 'True' },
  // { key: 'Ctrl+Shift+V', title: 'Paste Over Selection' },
  // { gap: 'True' },
  { key: 'Ctrl+A', title: 'Select All', onClick: shortcutActions.selectAll },
  { gap: 'True' },
  { key: 'Shift+R', title: 'Rename', onClick: shortcutActions.rename },
  { gap: 'True' },
  // { key: 'Ctrl+/', title: 'Toggle Hide/Show UI' },
  // { gap: 'True' },
  { key: 'Ctrl+Shift+F', title: 'Toggle Fullscreen' },
  { gap: 'True' },
  // { key: 'Ctrl++', title: 'Zoom In' },
  // { gap: 'True' },
  // { key: 'Ctrl+-', title: 'Zoom Out' },
  // { gap: 'True' },
  { key: 'F', title: 'Focus', onClick: shortcutActions.focus },
  { gap: 'True' },
  { key: 'R', title: 'Reset Camera', onClick: shortcutActions.resetCamera },
  { gap: 'True' },
  { key: 'Right Click + W/A/S/D/Q/E',title: 'Free camera' },
  { key: 'W / S', title: 'Move forward / back' },
  { key: 'A / D', title: 'Move left / right' },
  { key: 'E / Q', title: 'Move up / down' },
];
